// src/services/dashboardServices.js
import api from '../api/apiClient';
import { handleApiError } from '../utils/removeMockDataFallbacks';
import { analysisService } from './analysisService';

/**
 * Dashboard services for fetching analytics and metrics
 */
export const dashboardService = {
  /**
   * Get dashboard metrics for superadmin
   * @returns {Promise<Object>} Dashboard metrics data
   */
  getSuperAdminMetrics: async () => {
    try {
      // Use the dedicated superadmin metrics endpoint
      const response = await api.get('/dashboard/metrics/superadmin');

      if (response.success) {
        return response;
      } else {
        throw new Error('Failed to fetch superadmin metrics');
      }
    } catch (error) {
      console.error('Error fetching superadmin metrics:', error);
      throw error;
    }
  },

  /**
   * Get dashboard metrics for company provider
   * @param {string} companyId - Company ID (optional, uses current user's company if not provided)
   * @returns {Promise<Object>} Dashboard metrics data for the company
   */
  getProviderMetrics: async (companyId = null) => {
    try {
      const endpoint = companyId
        ? `/dashboard/metrics/provider/${companyId}`
        : '/dashboard/metrics/provider';

      return await api.get(endpoint);
    } catch (error) {
      console.error('Error fetching provider metrics:', error);
      throw error;
    }
  },

  /**
   * Get activity summary by time period (day, week, month)
   * @param {Object} params - Filter parameters
   * @param {string} params.period - Time period ('day', 'week', 'month')
   * @param {string} params.companyId - Company ID (optional)
   * @returns {Promise<Object>} Activity summary data
   */
  getActivitySummary: async (params = {}) => {
    try {
      return await api.get('/dashboard/activity-summary', params);
    } catch (error) {
      console.error('Error fetching activity summary:', error);
      throw error;
    }
  },



  /**
   * Get activity trends data for charts
   * @returns {Promise<Array>} Activity trends data
   */
  getActivityTrends: async () => {
    try {
      const response = await api.get('/logs', {
        params: {
          limit: 100,
          sort: '-timestamp',
          days: 7
        }
      });

      if (response.success && response.data) {
        // Process logs to create daily activity trends
        const logs = response.data;
        const trends = {};
        const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

        // Initialize trends for last 7 days
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dayName = days[date.getDay()];
          trends[dayName] = { name: dayName, login: 0, create: 0, update: 0, delete: 0 };
        }

        // Count activities by day and type
        logs.forEach(log => {
          const logDate = new Date(log.timestamp);
          const dayName = days[logDate.getDay()];

          if (trends[dayName]) {
            if (log.actionType.includes('LOGIN')) trends[dayName].login++;
            else if (log.actionType.includes('CREATE')) trends[dayName].create++;
            else if (log.actionType.includes('UPDATE')) trends[dayName].update++;
            else if (log.actionType.includes('DELETE')) trends[dayName].delete++;
          }
        });

        return Object.values(trends);
      }

      return [];
    } catch (error) {
      console.warn('Activity trends not available');
      return [];
    }
  },

  /**
   * Get user engagement data for charts
   * @returns {Promise<Array>} User engagement data
   */
  getUserEngagementData: async () => {
    try {
      const response = await api.get('/logs', {
        params: {
          limit: 200,
          sort: '-timestamp',
          days: 7
        }
      });

      if (response.success && response.data) {
        const logs = response.data;
        const engagement = {};

        // Process last 7 days
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dateStr = date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
          engagement[dateStr] = { date: dateStr, activeUsers: new Set(), newUsers: new Set() };
        }

        // Count unique active users per day
        logs.forEach(log => {
          const logDate = new Date(log.timestamp);
          const dateStr = logDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });

          if (engagement[dateStr]) {
            engagement[dateStr].activeUsers.add(log.userId);
            if (log.actionType.includes('CREATE') && log.actionType.includes('USER')) {
              engagement[dateStr].newUsers.add(log.userId);
            }
          }
        });

        // Convert sets to counts
        return Object.values(engagement).map(day => ({
          date: day.date,
          activeUsers: day.activeUsers.size,
          newUsers: day.newUsers.size
        }));
      }

      return [];
    } catch (error) {
      console.warn('User engagement data not available');
      return [];
    }
  },

  getHistoricalMetrics: async (days = 7) => {
    try {
      return await api.get(`/dashboard/metrics/provider/historical?days=${days}`);
    } catch (error) {
      console.error('Error fetching historical metrics:', error);
      throw error;
    }
  },

  /**
   * Get superadmin historical metrics
   * @param {number} days - Number of days for historical comparison
   * @returns {Promise<Object>} Historical metrics data
   */
  getSuperAdminHistoricalMetrics: async (days = 7) => {
    try {
      return await api.get(`/dashboard/metrics/superadmin/historical?days=${days}`);
    } catch (error) {
      console.error('Error fetching superadmin historical metrics:', error);
      throw error;
    }
  }
};

// Export enhanced companyService with additional metrics methods
export const enhanceCompanyService = (originalCompanyService) => {
  return {
    ...originalCompanyService,

    /**
     * Get detailed company metrics
     * @returns {Promise<Object>} Detailed metrics for all companies
     */
    getCompanyMetrics: async () => {
      try {
        return await api.get('/companies/metrics');
      } catch (error) {
        console.warn('Company metrics API not available');
        throw error;
      }
    }
  };
};